import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-md-surface text-md-on-surface border-md-outline-variant",
        destructive:
          "border-md-error-container bg-md-error-container/10 text-md-error [&>svg]:text-md-error *:data-[slot=alert-description]:text-md-error/90",
        success:
          "border-green-100 bg-green-50/50 text-green-600 [&>svg]:text-green-600 *:data-[slot=alert-description]:text-green-600/90",
        warning:
          "border-amber-100 bg-amber-50/50 text-amber-600 [&>svg]:text-amber-600 *:data-[slot=alert-description]:text-amber-600/90",
        info:
          "border-md-tertiary-container bg-md-tertiary-container/10 text-md-tertiary [&>svg]:text-md-tertiary *:data-[slot=alert-description]:text-md-tertiary/90",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-md-on-surface-variant col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed",
        className
      )}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription }
